/**
 * SATIŞ KANALI ROZETİ — siparişin nereden geldiğini gösterir.
 *
 * NİÇİN GEREKLİ: artık iki pazaryeri var ve ikisinin İŞ KURALLARI FARKLI.
 * Trendyol satıcıya dar bir kargolama penceresi tanıyor (birkaç gün); Demontha
 * ise sipariş üzerine 10 iş gününde üretiyor. Bu yüzden Trendyol'da yalnızca
 * stoktan gönderilebilen ürünler satılıyor ve o siparişler önceliklidir.
 * Ekipteki biri sipariş listesine baktığında bunu TEK BAKIŞTA görmeli.
 *
 * ── NİÇİN SADECE RENK DEĞİL ────────────────────────────────────────────────
 * Rozet hem işaret hem METİN taşır. Yalnızca renge/logoya dayalı bir ayrım
 * renk körü kullanıcıda ve yazdırılmış sayfada kaybolurdu.
 *
 * ── LOGOLAR ────────────────────────────────────────────────────────────────
 * Marka logoları dışarıdan yüklenmez: hem çevrimdışı çalışması gerekir hem de
 * üçüncü taraf sunucusuna istek atmak, ERP ekranında istenmeyen bir izleme
 * yüzeyi açar. Bu yüzden basit, tanınabilir SVG işaretler gömülüdür.
 */

export type Channel = "shopify" | "trendyol" | "manual" | "legacy";

/** Doküman alanından kanalı çıkarır — eski kayıtlarda alan yoktur. */
export function channelOf(o: {
  channel?: string | null;
  shopifyOrderId?: string | null;
  trendyol?: unknown;
  legacyWooId?: unknown;
}): Channel {
  const c = String(o.channel ?? "");
  // ★ "manual" tanınmazsa manuel sipariş "Eski sistem" rozetiyle görünür.
  if (c === "trendyol" || c === "shopify" || c === "manual" || c === "legacy") return c;
  if (o.trendyol) return "trendyol";
  if (o.shopifyOrderId) return "shopify";
  return "legacy";
}

const LABEL: Record<Channel, string> = {
  shopify: "Shopify",
  trendyol: "Trendyol",
  manual: "Manuel",
  legacy: "Eski sistem",
};

function ShopifyMark({ className = "" }: { className?: string }) {
  /**
   * Shopify'ın alışveriş torbası markası. Torbanın ayırt edici üç öğesi var ve
   * üçü de korunmuştur: sola doğru eğik gövde, sol üstteki kıvrık sap ve gövdeyi
   * delen "S". Bunlar olmadan şekil herhangi bir alışveriş torbasına benziyordu.
   *
   * Marka rengi (#95BF47 / #5E8E3E) sabittir: rozetin zemini değişse bile logo
   * kendi rengini korur — tanınırlık rengin kendisinde.
   */
  return (
    <svg viewBox="0 0 109 124" className={className} aria-hidden="true">
      <path
        fill="#95BF47"
        d="M74.7 14.8c-.1-.5-.5-.7-.8-.8-.4 0-8.2-.6-8.2-.6s-5.4-5.4-6-6c-.6-.6-1.7-.4-2.2-.3 0 0-1.1.4-3 1-.3-1-.8-2.3-1.4-3.6C51.2 1.6 48.3 0 44.8 0h-.4c-.1-.1-.2-.3-.4-.4C42.5-2 40.3-2.7 37.7-2.6c-5 .1-10 3.8-14 10.2-2.8 4.6-5 10.3-5.6 14.7-5.8 1.8-9.8 3-9.9 3.1-2.9.9-3 1-3.4 3.8C4.5 45.3-2 96.1-2 96.1l55 9.5 29.8-7.4S74.8 15.3 74.7 14.8zM53.4 9.5c-2.2.7-4.7 1.4-7.4 2.3v-1.6c0-4.8-.7-8.7-1.7-11.8 4.1.5 6.8 5.1 9.1 11.1zM40.9 -.6c1.2 2.6 2 6.3 2 11.3v.8c-4.8 1.5-10 3.1-15.3 4.7 3-11.4 8.6-17 12.3-16.8zM35.7 -8.6c.8 0 1.6.3 2.3.8-4.7 2.2-9.7 7.8-11.8 18.9-4.2 1.3-8.3 2.6-12.1 3.7C17 5.9 25.3 -8.4 35.7 -8.6z"
        transform="translate(2 12)"
      />
      <path
        fill="#5E8E3E"
        d="M73.9 14c-.4 0-8.2-.6-8.2-.6s-5.4-5.4-6-6c-.2-.2-.5-.3-.8-.4V105.6l29.8-7.4S74.8 15.3 74.7 14.8c-.1-.5-.4-.7-.8-.8z"
        transform="translate(2 12)"
      />
      <path
        fill="#FFF"
        d="M45.1 30.3l-3.5 12.9s-3.8-1.8-8.4-1.5c-6.8.4-6.8 4.7-6.8 5.8.4 5.8 15.6 7.1 16.5 20.7.7 10.7-5.7 18-14.9 18.6-11.1.7-17.2-5.8-17.2-5.8l2.3-10s6.1 4.6 11 4.3c3.2-.2 4.4-2.8 4.3-4.7-.5-7.6-12.9-7.1-13.7-19.5-.7-10.5 6.2-21.1 21.3-22 5.9-.4 8.9 1.1 8.9 1.1z"
        transform="translate(2 12)"
      />
    </svg>
  );
}

function TrendyolMark({ className = "" }: { className?: string }) {
  /**
   * Trendyol'un turuncu işareti: yukarı bakan kıvrık "kanca" ve altındaki
   * yuvarlatılmış gövde. Marka rengi #F27A1A.
   */
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden="true">
      <path
        fill="#F27A1A"
        d="M16 3.2c-4.3 0-8.4 1.1-12 3.1a1.15 1.15 0 0 0 1.1 2C8.4 6.6 12.1 5.5 16 5.5s7.6 1.1 10.9 2.8a1.15 1.15 0 0 0 1.1-2c-3.6-2-7.7-3.1-12-3.1z"
      />
      <path
        fill="#F27A1A"
        d="M16 9.2c-3.2 0-6.3.7-9.1 2v9.2c0 2.8 1.6 5.4 4.2 6.6l4.9 2.4 4.9-2.4c2.6-1.2 4.2-3.8 4.2-6.6v-9.2c-2.8-1.3-5.9-2-9.1-2zm-3.3 6.9v6.4h-3.2v-6.4h3.2zm5.9 0v6.4h-3.2v-6.4h3.2zm5.9 0v6.4h-3.2v-6.4h3.2z"
      />
    </svg>
  );
}

/**
 * Zemin BİLEREK soluk: logo kendi marka rengini taşıyor, rozetin arkası onunla
 * yarışmamalı. Metin rengi ise WCAG AA'yı geçecek kadar koyu tutuldu — rozet
 * yalnızca renkle değil, okunur bir etiketle de anlam taşır.
 */
const TONE: Record<Channel, string> = {
  shopify: "bg-green-50 text-green-900 ring-green-200",
  trendyol: "bg-orange-50 text-orange-900 ring-orange-200",
  manual: "bg-purple-50 text-purple-900 ring-purple-200",
  legacy: "bg-gray-50 text-gray-700 ring-gray-200",
};

export function ChannelBadge({
  channel,
  size = "sm",
  showLabel = true,
  className = "",
}: {
  channel: Channel;
  size?: "sm" | "md";
  /** Dar sütunlarda yalnızca işaret gösterilebilir; erişilebilir ad korunur. */
  showLabel?: boolean;
  className?: string;
}) {
  const pad = size === "md" ? "px-2.5 py-1 text-sm" : "px-2 py-0.5 text-xs";
  const icon = size === "md" ? "h-4 w-4" : "h-3.5 w-3.5";

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-md font-medium ring-1 ring-inset ${TONE[channel]} ${pad} ${className}`}
      title={LABEL[channel]}
    >
      {channel === "shopify" ? <ShopifyMark className={icon} /> : null}
      {channel === "trendyol" ? <TrendyolMark className={icon} /> : null}
      {showLabel ? LABEL[channel] : <span className="sr-only">{LABEL[channel]}</span>}
    </span>
  );
}

export default ChannelBadge;
